import OWRadioGroup from '../components/ow_radio_group/OWRadioGroup';
import {
	changeFilterValueToArrayOfIds,
	changeFilterValueToArrayOfValues,
	fetchWithDifferntSearchName,
	getEndDateFieldName,
	getObjectValues,
	getStartDateFieldName,
	getTimeZoneFieldName,
	nullSafeGet,
	nullSafeGetOrElse,
	getTupleNameFieldName,
	getTupleValueFieldName,
} from './DataAccessUtils';
import { PERMISSION_NAMES, isUserAllowedToAccess } from './AuthUtils';
import {
	FILTER_CONFIG_NAMES,
	FILTER_FIELD_TYPE,
	FILTER_SEPARATOR,
	FILTER_VALUE_TYPE,
	ROLE_TYPES,
} from './DataConstants';
import { DEFAULT_LABEL_COLOR } from '../constants/labelConstants';
import { Tag } from 'antd';
import { AreaTreeSelect } from '../components/area_tree_select';
import BuyerRegionsLocationsTreeSelect from '../components/regions_tree_select/RegionsLocationsTreeSelect';
import SupplierRegionsLocationsTreeSelect from '../components/regions_tree_select/SupplierRegionsLocationsTreeSelect';
import {
	capitalize,
	decodeUTCAdjustedDate,
	encodeUTCAdjustedDate,
	formatDateForParam,
	getContactName,
} from './DataFormatterUtils';
import { UserName } from '../components/name_component/user/UserNameComponent';
import OWAsyncTreeSelect from '../components/ow_async_tree_select/OWAsyncTreeSelect';
import React from 'react';
import ProblemTypeTreeSelect from '../components/problem_type_tree_select';
import AssetFilterRowDisplay from '../hooks/AssetFilterRowDisplay';
import OWDateRangePicker from '../components/ow_date_range_picker/OWDateRangePicker';
import OWAsyncTupleSelect from '../components/ow_async_select/OWAsyncTupleSelect';
import DebouncedFilterInput from '../components/common/DebouncedFilterInput';
import moment from 'moment';
import { FilterOutlined, TagFilled, StarFilled } from '@ant-design/icons';
import { isWorkOrderLabelsTrackEnabled } from '../data/workOrderStarLabels';
import { ACC_AUDIT_MATCH_STATUS_FILTER_OPTIONS } from './AssetCycleUtils';
import LabelsDropdown from '../components/labels_dropdown/LabelsDropdown';
import { LabelEntityType } from '../types/labels';

const renderLabelOption = (item: { name: string; color?: string }) => (
	<span className="inline-flex items-center gap-1.5">
		<TagFilled style={{ color: item.color || DEFAULT_LABEL_COLOR }} />
		<span>{item.name}</span>
	</span>
);

/** Static options for the Replacement Index asset filter. */
export const REPLACEMENT_INDEX_STATIC_OPTIONS = [
	{ id: 'repair', name: 'Low' },
	{ id: 'monitor', name: 'Medium' },
	{ id: 'replace', name: 'High' },
];

/** Flat tree options for Notes Status (same control pattern as Priority: OWAsyncTreeSelect + checkboxes). */
const NOTES_STATUS_STATIC_OPTIONS = [
	{ id: 'unread', name: 'Unread Notes' },
	{ id: 'awaitingMyResponse', name: 'Waiting on you' },
	{ id: 'awaitingResponse', name: 'Awaiting response' },
	{ id: 'awaitingResponseStale', name: 'Pending response [>7d]' },
];

const NOTES_STATUS_RECORDS = NOTES_STATUS_STATIC_OPTIONS.reduce(function (acc, opt) {
	acc[opt.id] = opt;
	return acc;
}, {});

/** Shared with workOrderHooks (attention dashboard / calendar) Notes Status tree filter. */
export const WORK_ORDER_NOTES_STATUS_TREE_OPTIONS = NOTES_STATUS_STATIC_OPTIONS;
export const workOrderNotesStatusTreeRecords = NOTES_STATUS_RECORDS;

/** Collapse tags to a count so the toolbar control stays a fixed width. */
export const WORK_ORDER_NOTES_STATUS_TREE_SELECT_PROPS = {
	maxTagCount: 0,
	maxTagPlaceholder: function (omittedValues) {
		return (
			<span className="inline-flex items-center gap-2">
				<FilterOutlined className="size-[12.5px]" />
				{'Notes status (' + omittedValues.length + ')'}
			</span>
		);
	},
	showSearch: false,
	showArrow: true,
	placement: 'bottomLeft',
	dropdownMatchSelectWidth: false,
	className:
		'!w-max !min-w-[145px] !flex-none [&_.ant-select-selector]:!pl-3  [&_.ant-select-selector]:cursor-pointer [&_.ant-select-selection-overflow]:!w-max [&_.ant-select-selection-overflow]:!flex-nowrap [&_.ant-select-selection-overflow-item]:!mr-0 [&_.ant-select-selection-overflow-item]:!shrink-0 [&_.ant-select-selection-overflow-item-rest]:!shrink-0 [&_.ant-select-selection-item]:!max-w-none [&_.ant-select-selection-item]:!overflow-visible [&_.ant-select-selection-item]:!border-0 [&_.ant-select-selection-item]:!bg-transparent [&_.ant-select-selection-item]:!shadow-none [&_.ant-select-selection-item]:!px-0 [&_.ant-select-selection-item]:!shrink-0 [&_.ant-select-selection-item]:text-left [&_.ant-select-selection-item]:whitespace-nowrap [&_.ant-select-selection-item-content]:!overflow-visible [&_.ant-select-selection-item-content]:whitespace-nowrap [&_.ant-select-arrow]:cursor-pointer [&_.ant-select-arrow]:!right-3 [&_.ant-select-selection-placeholder]:!text-[rgba(0,0,0,0.65)] [&_.ant-select-selection-placeholder]:!opacity-100 [&_.ant-select-selection-placeholder]:!flex [&_.ant-select-selection-placeholder]:!items-center [&_.ant-select-selection-placeholder]:!inset-0 [&_.ant-select-selection-placeholder]:!pl-3 [&_.ant-select-selection-placeholder]:!transform-none',
	dropdownClassName:
		'!w-auto !max-w-[420px] [&_.ant-select-tree-list]:!overflow-x-hidden [&_.ant-select-tree-list-holder-inner]:!w-full [&_.ant-select-tree-title]:!whitespace-normal [&_.ant-select-tree-title]:break-words [&_.ant-select-tree-switcher]:!hidden [&_.ant-select-tree-indent]:!hidden [&_.ant-select-tree-node-content-wrapper]:!block [&_.ant-select-tree-node-content-wrapper]:!w-full [&_.ant-select-tree-node-content-wrapper]:!pl-0 [&_.ant-select-tree-node-content-wrapper]:!whitespace-normal [&_.ant-select-tree-treenode]:!pl-1',
};

/** Selected ids → noteStatuses; all other static options → notNoteStatuses (for API). */
export function buildWorkOrderNotesStatusFilterPatch(selectedIds) {
	const allIds = NOTES_STATUS_STATIC_OPTIONS.map((o) => o.id);
	if (!selectedIds || !nullSafeGet('length', selectedIds)) {
		return {
			[FILTER_CONFIG_NAMES.NOTES_STATUS]: undefined,
			notNoteStatuses: undefined,
			noteStatus: undefined,
			notNoteStatus: undefined,
		};
	}
	const selectedSet = {};
	selectedIds.forEach(function (id) {
		selectedSet[id] = true;
	});
	const unselected = allIds.filter(function (id) {
		return !selectedSet[id];
	});
	return {
		[FILTER_CONFIG_NAMES.NOTES_STATUS]: selectedIds.join(','),
		noteStatus: undefined,
		notNoteStatus: undefined,
	};
}

export const INITIAL_ACTIVE_FILTERS = {
	noActiveFilter: true,
	isActive: 'true',
};

export const INITIAL_IS_ACTIVE_FILTERS = {
	noIsActiveFilter: true,
	isActive: 'true',
};
export const INITIAL_DOWNTIME_ASSET_FILTERS = {
	noActiveFilter: true,
	isActive: 'true',
	downtimeStatus: 'offline',
	rightNow: true,
};
export const INITIAL_READY_FOR_REPLACEMENT_ASSET_FILTERS = {};

export const IS_ACTIVE_FILTER_ITEMS = (isReduxTableWithHeader) => [
	{
		label: 'All',
		value: isReduxTableWithHeader ? 'undefined' : undefined,
	},
	{
		label: 'Active',
		value: 'true',
	},
	{
		label: 'Inactive',
		value: 'false',
	},
];

export const getIsActiveFilterField: any = (
	updateFilters,
	tcName,
	isReduxTableWithHeader = false
) => ({
	fieldName: 'isActive',
	type: FILTER_FIELD_TYPE.RADIO_GROUP,
	items: IS_ACTIVE_FILTER_ITEMS(isReduxTableWithHeader),
	filtersValueAccessor: (filters) => {
		const value = nullSafeGet('isActive', filters);
		// Header-variant items use string values, so stringify to match the radio buttons.
		if (isReduxTableWithHeader) {
			return typeof value === 'undefined' ? 'undefined' : `${value}`;
		}
		return value;
	},
	defaultValue: undefined,
	keyAccessor: (item) => item.value,
	valueAccessor: (item) => item.value,
	handleChange: (isActive) => {
		updateFilters({ isActive: isActive === 'undefined' ? undefined : isActive }, tcName);
	},
	renderRecord: (item) => item.label,
	component: OWRadioGroup,
	showOutsideDropdown: true,
});

export const FILTER_OPTIONS = {
	IS: 'is',
	IS_NOT: 'isNot',
	CONTAINS: 'contains',
};

export const FILTER_OPTION_IS = { value: FILTER_OPTIONS.IS, label: 'Is' };
export const FILTER_OPTION_IS_NOT = { value: FILTER_OPTIONS.IS_NOT, label: 'Is Not' };
export const FILTER_OPTION_CONTAINS = { value: FILTER_OPTIONS.CONTAINS, label: 'Contains' };
export const FILTER_OPTIONS_IS_AND_IS_NOT = [FILTER_OPTION_IS, FILTER_OPTION_IS_NOT];

export const FILTER_OPTIONS_DISPLAY = {
	[FILTER_OPTIONS.IS]: 'is',
	[FILTER_OPTIONS.IS_NOT]: 'is not',
	[FILTER_OPTIONS.CONTAINS]: 'contains',
};

export const FILTER_OPTION_KEY_BUILDERS = {
	[FILTER_OPTIONS.IS]: (fieldName) => fieldName,
	[FILTER_OPTIONS.IS_NOT]: (fieldName) => `not${capitalize(fieldName)}`,
	[FILTER_OPTIONS.CONTAINS]: (fieldName) => `contains${capitalize(fieldName)}`,
};

// Build once at module load to save time
const REVERSE_FIELD_NAME_MAP: Record<string, string> = {};

Object.values(FILTER_CONFIG_NAMES).forEach((fieldName) => {
	Object.values(FILTER_OPTIONS).forEach((option) => {
		const activeFieldName = FILTER_OPTION_KEY_BUILDERS[option](fieldName);
		REVERSE_FIELD_NAME_MAP[activeFieldName] = fieldName;
	});
});

export const getFieldNameFromActiveFieldName = (activeFieldName: string): string => {
	return REVERSE_FIELD_NAME_MAP[activeFieldName] || activeFieldName;
};

export const getTagRenderStyle = (filterOption) => {
	switch (filterOption) {
		case FILTER_OPTIONS.IS_NOT:
			return { style: { backgroundColor: '#FFF1F0' } };
		case FILTER_OPTIONS.CONTAINS:
			return {};
		default:
			return {};
	}
};

export const getTagRender = (filterOption) => (props) => {
	const { label = '', ...otherProps } = props;
	return (
		<Tag {...getTagRenderStyle(filterOption)} {...otherProps}>
			{label}
		</Tag>
	);
};

export const filterOptionsBuilder = (selectOption, fieldName, ids, separator = ',') => {
	return Object.values(FILTER_OPTIONS).reduce((acc, option) => {
		const keyBuilder = FILTER_OPTION_KEY_BUILDERS[option];
		acc[keyBuilder(fieldName)] =
			selectOption === option && nullSafeGetOrElse('length', ids, 0) > 0
				? ids.join(separator)
				: undefined;
		return acc;
	}, {});
};

export const getFilters: any = ({
	filterTargetCollectionName = 'workOrdersAdvancedFilters',
	overviewCollectionName = 'workOrdersIndex',
	updateFilters,
	userType,
	currentUser,

	//States
	companyConfig,
	locations,
	areas,
	workOrderPriorities,
	assetTypes,
	problemTypes,
	supplierFacilities,
	assets,
	spendCategories,
	resolutionTypes,
	users,
	workOrderLabels,
	planned_maintenance_schedules,
	brands,
	depreciationClasses,
	regions,
	locationTypes,
	plannedMaintenanceTemplates,
	meterTypes,
	vendors,
	labels,
	//Fetches
	fetchSpendCategories,
	fetchMultipleSpendCategories,
	fetchWorkOrderPriorities,
	fetchMultipleWorkOrderPriorities,
	fetchWorkOrderLabels,
	fetchMultipleWorkOrderLabels,
	fetchAssetTypes,
	fetchMultipleAssetTypes,
	fetchAssets,
	fetchMultipleAssets,
	fetchResolutionTypes,
	fetchMultipleResolutionTypes,
	fetchMultipleLocations,
	fetchLocationsWithHierarchy,
	fetchSupplierFacilitiesInPrivateNetwork,
	fetchMultipleSupplierFacilitiesInPrivateNetwork,
	fetchProblemTypes,
	fetchMultipleProblemTypes,
	fetchAreas,
	fetchAreasDistinct,
	fetchAreasWithHierarchy,
	fetchMultipleAreas,
	fetchUsers,
	fetchMultipleUsers,
	fetchPMSchedule,
	fetchMultiplePMSchedule,
	fetchBrands,
	fetchMultipleBrands,
	fetchDepreciationClasses,
	fetchMultipleDepreciationClasses,
	fetchRegionsWithChildren,
	fetchMultipleRegions,
	fetchLocationTypes,
	fetchMultipleLocationTypes,
	fetchPlannedMaintenanceTemplates,
	fetchMultiplePlannedMaintenanceTemplates,
	fetchMeterTypes,
	fetchMultipleMeterTypes,
	fetchVendors,
	fetchMultipleVendors,
	fetchLabels,
	fetchMultipleLabels,
	contractorClients,
	fetchContractorClients,
	contractorLocations,
	fetchContractorLocations,
	fetchMultipleContractorLocations,
	contractorPrivateNetworks,
	fetchContractors,
	fieldTechs,
	fetchFieldTechs,
	fetchMultipleFieldTechs,
	assetTypesAdditionalFields,
	fetchAssetTypesAdditionalFields,
	fetchMultipleAssetTypesAdditionalFields,
	customers,
	fetchCustomers,
	siteSurveysGrouped,
	fetchSiteSurveysGrouped,
}) => {
	const LocationComponent =
		userType === ROLE_TYPES.SUPPLIER
			? SupplierRegionsLocationsTreeSelect
			: BuyerRegionsLocationsTreeSelect;

	const canViewWorkOrderLabels = isUserAllowedToAccess(userType)(
		PERMISSION_NAMES.VIEW_WORK_ORDER_LABELS,
		companyConfig,
		nullSafeGet('roles', currentUser)
	);

	const OWAsyncMultiSelectFilter = ({
		label,
		fieldName,
		placeholderText,
		stateSlice,
		fetch,
		fetchMultiple = () => {},
		keyAccessor = (item) => item.id,
		valueAccessor = (item) => item.id,
		renderItem = (item) => item.name,
		filtersValueAccessor = undefined,
		handleChange = undefined,
		mode = 'multiple',
		filterOptions = [
			{ value: FILTER_OPTIONS.IS, label: 'is' },
			{ value: FILTER_OPTIONS.IS_NOT, label: 'is not' },
		],
		valueType = undefined,
		...extra
	}) => ({
		label,
		fieldName,
		placeholderText,
		stateSlice,
		fetch,
		fetchMultiple,
		keyAccessor,
		valueAccessor,
		renderItem,
		filtersValueAccessor: filtersValueAccessor
			? filtersValueAccessor
			: (filtersStateSlice, selectedFilterOption = FILTER_OPTIONS.IS) =>
					(valueType === FILTER_VALUE_TYPE.STRING
						? changeFilterValueToArrayOfValues
						: changeFilterValueToArrayOfIds)(
						nullSafeGet(
							FILTER_OPTION_KEY_BUILDERS[selectedFilterOption](fieldName),
							filtersStateSlice
						)
					),
		handleChange: handleChange
			? handleChange
			: (selectOption = FILTER_OPTIONS.IS) =>
					(ids) =>
						updateFilters(filterOptionsBuilder(selectOption, fieldName, ids)),
		targetCollectionName: filterTargetCollectionName,
		mode,
		valueType,
		filterOptions,
		...extra,
	});

	const DateRangeSelectFilter = ({ label, labelPrefix, fieldName, ...extra }) => ({
		label,
		labelPrefix,
		fieldName,
		type: FILTER_FIELD_TYPE.DATE_RANGE,
		filtersValueAccessor: (filtersStateSlice) => {
			const timeZone = filtersStateSlice[getTimeZoneFieldName(fieldName)];
			const startDate = decodeUTCAdjustedDate(
				filtersStateSlice[getStartDateFieldName(fieldName)],
				false,
				timeZone
			);
			const endDate = decodeUTCAdjustedDate(
				filtersStateSlice[getEndDateFieldName(fieldName)],
				true,
				timeZone
			);
			return [startDate && moment(startDate), endDate && moment(endDate), timeZone];
		},
		handleChange: (values, tz = null) => {
			const startDate = values && values.length > 1 && values[0];
			const endDate = values && values.length > 1 && values[1];
			updateFilters({
				[getStartDateFieldName(fieldName)]: startDate
					? formatDateForParam(encodeUTCAdjustedDate(startDate, false, tz))
					: undefined,
				[getEndDateFieldName(fieldName)]: endDate
					? formatDateForParam(encodeUTCAdjustedDate(endDate, true, tz))
					: undefined,
				[getTimeZoneFieldName(fieldName)]: ((startDate || endDate) && tz) || undefined,
			});
		},
		renderRecord: (item) => item,
		valueAccessor: (item) => item,
		showTimeZone: true,
		component: OWDateRangePicker,
		...extra,
	});

	const OWRadioGroupFilter = ({
		fieldName,
		items,
		filtersValueAccessor = undefined,
		handleChange = undefined,
		info = [],
		...extra
	}) => ({
		type: FILTER_FIELD_TYPE.RADIO_GROUP,
		fieldName,
		items,
		component: OWRadioGroup,
		keyAccessor: (option) => option.value,
		valueAccessor: (option) => option.value,
		filtersValueAccessor: filtersValueAccessor
			? filtersValueAccessor
			: (filtersStateSlice) => {
					const val = filtersStateSlice[fieldName];
					return typeof val === 'undefined' ? undefined : `${val}`;
			  },
		handleChange: handleChange
			? handleChange
			: (val) => {
					const boolVal = val === 'true' ? true : val === 'false' ? false : undefined;
					updateFilters({ [fieldName]: boolVal });
			  },
		targetCollectionName: filterTargetCollectionName,
		renderItem: (option) => option.label,
		renderRecord: (item) => item.label,
		info,
		...extra,
	});

	const OWGenericMultiSelectFilter = ({
		label,
		fieldName,
		options,
		icon = undefined,
		treeSelectProps: customTreeSelectProps = {},
		handleChange: customHandleChange,
		...extra
	}) => {
		const records = options.reduce(function (acc, opt) {
			acc[opt.id] = opt;
			return acc;
		}, {});

		return {
			label,
			fieldName,
			placeholder: icon ? (
				<span className="inline-flex items-center gap-1">
					{icon}
					{label}
				</span>
			) : (
				label
			),
			showOutsideDropdown: true,
			allowClear: false,
			treeSelectProps: customTreeSelectProps,
			stateSlice: {
				fetching: false,
				records,
				[filterTargetCollectionName]: {
					recordsIds: options.map(function (o) {
						return o.id;
					}),
				},
			},
			valueType: FILTER_VALUE_TYPE.STRING,
			filterOptions: [],
			filtersValueAccessor: function (filtersStateSlice) {
				var raw = nullSafeGet(fieldName, filtersStateSlice);
				if (!raw && raw !== 0) return undefined;
				if (Array.isArray(raw)) {
					return raw.filter(function (_) {
						return !!_;
					});
				}
				return ('' + raw).split(',').filter(function (_) {
					return !!_;
				});
			},
			handleChange: customHandleChange,
			fetch: function () {
				return Promise.resolve(options);
			},
			fetchMultiple: function () {
				return Promise.resolve();
			},
			component: OWAsyncTreeSelect,
			mode: 'multiple',
			keyAccessor: function (item) {
				return item.id;
			},
			valueAccessor: function (item) {
				return item.id;
			},
			renderItem: function (item) {
				return item.name;
			},
			labelAccessor: function (item) {
				return item.name;
			},
			targetCollectionName: filterTargetCollectionName,
			...extra,
		};
	};

	// Areas belong to a location, so when the user has selected location(s) in the Location filter we
	// scope the area options to those locations (the area endpoints accept a comma-separated
	// `locationIds` filter). Shared by both area filters so the behavior is identical everywhere.
	// These receive the unwrapped active filters (the table layer hands them `[tc].filters`).
	const areasLocationScopeAccessor = (activeFilters) => {
		const locationIds = nullSafeGet(FILTER_CONFIG_NAMES.LOCATION, activeFilters);
		return locationIds ? { locationIds } : {};
	};
	const areasLocationScopeNoteAccessor = (activeFilters) => {
		const selectedLocationIds = `${nullSafeGetOrElse(
			FILTER_CONFIG_NAMES.LOCATION,
			activeFilters,
			''
		)}`
			.split(',')
			.filter(Boolean);
		if (selectedLocationIds.length === 0) return undefined;
		if (selectedLocationIds.length === 1) {
			const locationRecords = nullSafeGetOrElse('records', locations, {});
			const locationName = nullSafeGet('name', locationRecords[selectedLocationIds[0]]);
			return locationName
				? `Areas are filtered based on ${locationName} location`
				: 'Areas are filtered based on 1 location';
		}
		return `Areas are filtered based on ${selectedLocationIds.length} locations`;
	};

	const staleDays = nullSafeGetOrElse('config.workOrder.stale.afterXDays', companyConfig, 14);
	return {
		[FILTER_CONFIG_NAMES.LOCATION]: OWAsyncMultiSelectFilter({
			label: 'Location',
			fieldName: FILTER_CONFIG_NAMES.LOCATION,
			placeholderText: 'Search for locations',
			stateSlice: locations,
			fetch: fetchLocationsWithHierarchy,
			fetchMultiple: fetchMultipleLocations,
			filtersValueAccessor: (activeFilters, selectedFilterOption = FILTER_OPTIONS.IS) => {
				if (selectedFilterOption === FILTER_OPTIONS.IS) {
					const locationIds = nullSafeGet(`locationIds`, activeFilters);
					const finalIds = nullSafeGetOrElse(`regionIds`, activeFilters, locationIds);
					return changeFilterValueToArrayOfIds(finalIds);
				}
				return changeFilterValueToArrayOfIds(
					nullSafeGet(
						FILTER_OPTION_KEY_BUILDERS[selectedFilterOption](FILTER_CONFIG_NAMES.LOCATION),
						activeFilters
					)
				);
			},
			handleChange:
				(selectOption = FILTER_OPTIONS.IS) =>
				(ids) => {
					const activeKey = FILTER_OPTION_KEY_BUILDERS[selectOption](FILTER_CONFIG_NAMES.LOCATION);
					const clearedKeys = Object.values(FILTER_OPTIONS).reduce((acc, option) => {
						acc[FILTER_OPTION_KEY_BUILDERS[option](FILTER_CONFIG_NAMES.LOCATION)] = undefined;
						return acc;
					}, {});
					updateFilters({
						...clearedKeys,
						regionIds: undefined,
						[activeKey]: ids.join(',') || undefined,
					});
				},
			component: LocationComponent,
		}),
		[FILTER_CONFIG_NAMES.AREA]: OWAsyncMultiSelectFilter({
			label: 'Area',
			fieldName: FILTER_CONFIG_NAMES.AREA,
			placeholderText: 'Search for areas',
			stateSlice: areas,
			filtersValueAccessor: (filtersStateSlice, selectedFilterOption = FILTER_OPTIONS.IS) => {
				const key = FILTER_OPTION_KEY_BUILDERS[selectedFilterOption](FILTER_CONFIG_NAMES.AREA);
				const areaNames = nullSafeGet(key, filtersStateSlice);
				return `${areaNames || ''}`.split(FILTER_SEPARATOR.TILDE).filter((_) => !!_);
			},
			handleChange:
				(selectOption = FILTER_OPTIONS.IS) =>
				(areaNames) => {
					const activeKey = FILTER_OPTION_KEY_BUILDERS[selectOption](FILTER_CONFIG_NAMES.AREA);
					const clearedKeys = Object.values(FILTER_OPTIONS).reduce((acc, option) => {
						acc[FILTER_OPTION_KEY_BUILDERS[option](FILTER_CONFIG_NAMES.AREA)] = undefined;
						return acc;
					}, {});
					updateFilters({
						...clearedKeys,
						[activeKey]: areaNames.join(FILTER_SEPARATOR.TILDE) || undefined,
					});
				},
			fetch: fetchWithDifferntSearchName(fetchAreasDistinct, 'search'),
			keyAccessor: (area) => area.hierarchyName,
			valueAccessor: (area) => area.hierarchyName,
			renderItem: (area) => area.hierarchyName,

			//extra
			labelAccessor: (area) => area.hierarchyName,
			type: FILTER_FIELD_TYPE.AREA,
			valueType: FILTER_VALUE_TYPE.STRING,
			separator: FILTER_SEPARATOR.TILDE,
			sortBy: { sort_by: 'name', order: 'ascend' },
			isSpecial: true,
			component: AreaTreeSelect,
			additionalFiltersAccessor: areasLocationScopeAccessor,
			noteAccessor: areasLocationScopeNoteAccessor,
		}),
		[FILTER_CONFIG_NAMES.PRIORITY]: OWAsyncMultiSelectFilter({
			label: 'Priority',
			fieldName: FILTER_CONFIG_NAMES.PRIORITY,
			placeholderText: 'Select a priority level',
			stateSlice: workOrderPriorities,
			fetch: fetchWorkOrderPriorities,
			fetchMultiple: fetchMultipleWorkOrderPriorities,

			component: OWAsyncTreeSelect,
		}),
		[FILTER_CONFIG_NAMES.ASSET_TYPE]: OWAsyncMultiSelectFilter({
			label: 'Asset Type',
			fieldName: FILTER_CONFIG_NAMES.ASSET_TYPE,
			placeholderText: 'Select asset type',
			stateSlice: assetTypes,
			fetch: fetchAssetTypes,
			fetchMultiple: fetchMultipleAssetTypes,

			component: OWAsyncTreeSelect,
		}),
		[FILTER_CONFIG_NAMES.PROBLEM_TYPE]: OWAsyncMultiSelectFilter({
			label: 'Problem Type',
			fieldName: FILTER_CONFIG_NAMES.PROBLEM_TYPE,
			placeholderText: 'Search for a problem type',
			stateSlice: problemTypes,
			renderItem: (problemType) => problemType.hierarchyName,
			fetch: fetchProblemTypes,

			//extra
			component: ProblemTypeTreeSelect,
		}),
		[FILTER_CONFIG_NAMES.SUPPLIER]: OWAsyncMultiSelectFilter({
			label: 'Supplier',
			fieldName: FILTER_CONFIG_NAMES.SUPPLIER,
			placeholderText: 'Search for a supplier',
			stateSlice: supplierFacilities,
			fetch: fetchSupplierFacilitiesInPrivateNetwork,
		}),
		[FILTER_CONFIG_NAMES.ASSET]: OWAsyncMultiSelectFilter({
			label: 'Asset',
			fieldName: FILTER_CONFIG_NAMES.ASSET,
			placeholderText: 'Search for an asset',
			stateSlice: assets,
			fetch: fetchWithDifferntSearchName(fetchAssets, 'search'),
			//fetchMultiple: fetchWithDifferntSearchName(fetchMultipleAssets, 'search'),
			renderItem: (asset) => <AssetFilterRowDisplay asset={asset} />,
			renderSelectedText: (asset) => asset.name,
			//sortBy: { sort_by: 'name', order: 'ascend' },
		}),
		//[FILTER_CONFIG_NAMES.PM_TEMPLATE]:,
		//[FILTER_CONFIG_NAMES.PART_EQUIPMENT_VENDOR]:,
		[FILTER_CONFIG_NAMES.VENDOR]: OWAsyncMultiSelectFilter({
			label: 'Vendor',
			fieldName: FILTER_CONFIG_NAMES.VENDOR,
			placeholderText: 'Search for a vendor',
			stateSlice: vendors,
			fetch: fetchVendors,
			fetchMultiple: fetchMultipleVendors,
		}),
		//[FILTER_CONFIG_NAMES.STOCK_LOCATION]:,
		//[FILTER_CONFIG_NAMES.EQUIPMENT]:,
		//[FILTER_CONFIG_NAMES.PART]:,
		//[FILTER_CONFIG_NAMES.EQUIPMENT_TYPE]:,
		[FILTER_CONFIG_NAMES.CREATED_BY]: OWAsyncMultiSelectFilter({
			label: 'Created by',
			fieldName: FILTER_CONFIG_NAMES.CREATED_BY,
			placeholderText: 'Search for a created by user',
			stateSlice: users,
			fetch: fetchWithDifferntSearchName(fetchUsers, 'search'),
			fetchMultiple: fetchMultipleUsers,
			keyAccessor: (ft) => nullSafeGetOrElse('contact.email', ft, ft.id),
			valueAccessor: (ft) => nullSafeGetOrElse('contact.email', ft, ft.id),
			renderItem: (ft) => (
				<UserName
					mode="dropdown"
					allowHover={false}
					contact={nullSafeGet('contact', ft)}
					account={nullSafeGet('account', ft)}
				/>
			),
			valueType: FILTER_VALUE_TYPE.STRING,
			labelAccessor: (ft) => getContactName(nullSafeGet('contact', ft)),
			renderSelectedText: (user) => getContactName(nullSafeGet('contact', user)),
		}),
		[FILTER_CONFIG_NAMES.NOTES_STATUS]: OWGenericMultiSelectFilter({
			label: 'Notes status',
			fieldName: FILTER_CONFIG_NAMES.NOTES_STATUS,
			options: NOTES_STATUS_STATIC_OPTIONS,
			icon: <FilterOutlined className="text-sm" />,
			treeSelectProps: WORK_ORDER_NOTES_STATUS_TREE_SELECT_PROPS,
			handleChange: (ids) => {
				updateFilters(buildWorkOrderNotesStatusFilterPatch(ids));
			},
		}),
		[FILTER_CONFIG_NAMES.MATCH_STATUS]: OWGenericMultiSelectFilter({
			label: 'Match Status',
			fieldName: FILTER_CONFIG_NAMES.MATCH_STATUS,
			options: ACC_AUDIT_MATCH_STATUS_FILTER_OPTIONS,
			placeholderText: 'Match Status',
			showOutsideDropdown: false,
			handleChange: (ids) => {
				updateFilters({
					[FILTER_CONFIG_NAMES.MATCH_STATUS]: ids && ids.length ? ids.join(',') : undefined,
				});
			},
		}),
		// Labels + Starred filters are gated behind the workorder-labels-track flag. Spreading
		// nothing when off means consumers referencing these filter names just skip them.
		// Labels has an additional, independent gate on top of the flag: VIEW_WORK_ORDER_LABELS.
		// Starred ("Tracked/Not Tracked") is a separate feature and stays flag-only.
		...(isWorkOrderLabelsTrackEnabled()
			? {
					...(canViewWorkOrderLabels
						? {
								[FILTER_CONFIG_NAMES.WO_LABELS]: OWAsyncMultiSelectFilter({
									label: 'Labels',
									fieldName: FILTER_CONFIG_NAMES.WO_LABELS,
									placeholderText: 'Labels',
									// Async slice + paginated fetch so the dropdown supports search + infinite
									// scroll (the catalogue can have far more than one page of labels).
									stateSlice: workOrderLabels,
									fetch: fetchWorkOrderLabels,
									fetchMultiple: fetchMultipleWorkOrderLabels,
									// Read options reactively from the SAME collection that label creation writes
									// to (WorkOrderLabelsManagementPage / WorkOrderAddLabelModal -> create into
									// 'workOrderLabelsIndex'), so a newly created label appears here instantly.
									targetCollectionName: 'workOrderLabelsIndex',
									// LabelsDropdown keys/checks tree nodes by the raw record `id` (numeric),
									// so the selected values must also be numeric for the checkboxes to reflect
									// the current selection.
									keyAccessor: (label) => label.id,
									valueAccessor: (label) => label.id,
									renderItem: (label) =>
										renderLabelOption({ name: label.label, color: label.color }),
									renderRecord: (label) =>
										renderLabelOption({ name: label.label, color: label.color }),
									labelAccessor: (label) =>
										renderLabelOption({ name: label.label, color: label.color }),
									renderSelectedText: (label: any) => (label && label.label) || '',
									pillLabel: 'Label',
									filtersValueAccessor: (
										filtersStateSlice,
										selectedFilterOption = FILTER_OPTIONS.IS
									) => {
										const key = FILTER_OPTION_KEY_BUILDERS[selectedFilterOption](
											FILTER_CONFIG_NAMES.WO_LABELS
										);
										const raw = nullSafeGet(key, filtersStateSlice);
										if (!raw) return [];
										const values = Array.isArray(raw) ? raw : ('' + raw).split(',');
										return values.filter((value) => value !== '' && value != null).map(Number);
									},
									handleChange:
										(selectOption = FILTER_OPTIONS.IS) =>
										(ids) => {
											const activeKey = FILTER_OPTION_KEY_BUILDERS[selectOption](
												FILTER_CONFIG_NAMES.WO_LABELS
											);
											const clearedKeys = Object.values(FILTER_OPTIONS).reduce((acc, option) => {
												acc[FILTER_OPTION_KEY_BUILDERS[option](FILTER_CONFIG_NAMES.WO_LABELS)] =
													undefined;
												return acc;
											}, {});
											updateFilters({
												...clearedKeys,
												[activeKey]: ids && ids.length ? ids.join(',') : undefined,
											});
										},
									component: LabelsDropdown,
								}),
						  }
						: {}),
					[FILTER_CONFIG_NAMES.STARRED]: OWRadioGroupFilter({
						fieldName: FILTER_CONFIG_NAMES.STARRED,
						items: [
							{ label: 'All', value: undefined },
							{
								label: (
									<span className="inline-block align-middle">
										<span className="inline-flex items-center gap-2 leading-none">
											<span className="box-border inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border border-solid border-calendulaGold-5 bg-calendulaGold-5">
												<StarFilled className="text-sm leading-none !text-white" />
											</span>
											<span className="bodyregular">Tracked</span>
										</span>
									</span>
								),
								value: 'true',
							},
							{ label: 'Not Tracked', value: 'false' },
						],
						info: [
							{
								title: 'Tracked',
								description: 'Work orders you have tracked.',
							},
							{
								title: 'Not Tracked',
								description: 'Work orders you have not tracked.',
							},
						],
					}),
			  }
			: {}),
		//[FILTER_CONFIG_NAMES.APPROVED_BY]:,
		//[FILTER_CONFIG_NAMES.DEPRECIATION_CLASS]:,
		//[FILTER_CONFIG_NAMES.SHIP_TO_LOCATION]:,
		[FILTER_CONFIG_NAMES.BRAND]: OWAsyncMultiSelectFilter({
			label: 'Brands',
			fieldName: FILTER_CONFIG_NAMES.BRAND,
			placeholderText: 'Search for an asset',
			stateSlice: brands,
			fetch: fetchBrands,
			fetchMultiple: fetchMultipleBrands,
			renderItem: (brand) => brand.brandName,
			renderRecord: (brand) => brand.brandName,
			labelAccessor: (brand) => brand.brandName,
			component: OWAsyncTreeSelect,
		}),
		[FILTER_CONFIG_NAMES.CUSTOMERS]: OWAsyncMultiSelectFilter({
			label: 'Customers',
			fieldName: FILTER_CONFIG_NAMES.CUSTOMERS,
			placeholderText: 'Search for a customer',
			stateSlice: customers,
			fetch: fetchCustomers,
			fetchMultiple: () => {},
			renderItem: (customer) => customer.displayName || customer.name,
			component: OWAsyncTreeSelect,
		}),
		//[FILTER_CONFIG_NAMES.DEFAULT_VENDORS]:,

		[FILTER_CONFIG_NAMES.TRADE]: OWAsyncMultiSelectFilter({
			label: 'Trade',
			fieldName: FILTER_CONFIG_NAMES.TRADE,
			placeholderText: 'Search for a trade',
			stateSlice: spendCategories,
			fetch: fetchSpendCategories,
			fetchMultiple: fetchMultipleSpendCategories,
			renderItem: (spendCategory) => spendCategory.name,
		}),
		[FILTER_CONFIG_NAMES.CONTRACTOR_CLIENT]: {
			label: 'Clients',
			fieldName: FILTER_CONFIG_NAMES.CONTRACTOR_CLIENT,
			placeholderText: 'Search for a client',
			stateSlice: contractorClients,
			fetch: fetchContractorClients,
			fetchMultiple: () => {},
			keyAccessor: (cc) => cc.id,
			valueAccessor: (cc) => `${cc.id}`,
			renderItem: (cc) => cc.name,
			filterOptions: [
				{ value: FILTER_OPTIONS.IS, label: 'is' },
				{ value: FILTER_OPTIONS.IS_NOT, label: 'is not' },
			],
			filtersValueAccessor: (filtersStateSlice, selectedFilterOption = FILTER_OPTIONS.IS) =>
				nullSafeGet(
					FILTER_OPTION_KEY_BUILDERS[selectedFilterOption](FILTER_CONFIG_NAMES.CONTRACTOR_CLIENT),
					filtersStateSlice
				),
			handleChange:
				(selectOption = FILTER_OPTIONS.IS) =>
				(contractorClientId) => {
					const clearedKeys = Object.values(FILTER_OPTIONS).reduce((acc, option) => {
						acc[FILTER_OPTION_KEY_BUILDERS[option](FILTER_CONFIG_NAMES.CONTRACTOR_CLIENT)] =
							undefined;
						return acc;
					}, {});
					const activeKey = FILTER_OPTION_KEY_BUILDERS[selectOption](
						FILTER_CONFIG_NAMES.CONTRACTOR_CLIENT
					);
					updateFilters({
						...clearedKeys,
						[activeKey]: contractorClientId || undefined,
					});
				},
			targetCollectionName: filterTargetCollectionName,
			sortBy: { sort_by: 'name', order: 'ascend' },
		},
		[FILTER_CONFIG_NAMES.CONTRACTOR_LOCATION]: OWAsyncMultiSelectFilter({
			label: 'Locations',
			fieldName: FILTER_CONFIG_NAMES.CONTRACTOR_LOCATION,
			placeholderText: 'Search for a location',
			stateSlice: contractorLocations,
			fetch: fetchContractorLocations,
			fetchMultiple: fetchMultipleContractorLocations,
			renderItem: (cl) => cl.name,
			sortBy: { sort_by: 'name', order: 'ascend' },
		}),
		[FILTER_CONFIG_NAMES.SUB_CONTRACTORS]: OWAsyncMultiSelectFilter({
			label: 'Sub Contractors',
			fieldName: FILTER_CONFIG_NAMES.SUB_CONTRACTORS,
			placeholderText: 'Search for a sub contractor',
			stateSlice: contractorPrivateNetworks,
			fetch: fetchWithDifferntSearchName(fetchContractors, 'search'),
			fetchMultiple: () => {},
			renderItem: (item) => nullSafeGet('supplierFacility.name', item) || nullSafeGet('name', item),
			keyAccessor: (item) => nullSafeGet('supplierFacility.id', item),
			valueAccessor: (item) => nullSafeGet('supplierFacility.id', item),
			sortBy: { sort_by: 'name', order: 'ascend' },
		}),
		[FILTER_CONFIG_NAMES.TECHNICIAN]: OWAsyncMultiSelectFilter({
			label: 'Technician',
			fieldName: FILTER_CONFIG_NAMES.TECHNICIAN,
			placeholderText: 'Search for a technician',
			stateSlice: fieldTechs,
			fetch: fetchFieldTechs,
			fetchMultiple: fetchMultipleFieldTechs,
			renderItem: (fieldTech) => fieldTech.name,
			keyAccessor: (fieldTech) => fieldTech.email,
			valueAccessor: (fieldTech) => fieldTech.email,
			sortBy: { sort_by: 'name', order: 'ascend' },
			valueType: FILTER_VALUE_TYPE.STRING,
			mode: null,
			filtersValueAccessor: (filtersStateSlice, selectedFilterOption) => {
				const val = nullSafeGet(
					FILTER_OPTION_KEY_BUILDERS[selectedFilterOption || FILTER_OPTIONS.IS](
						FILTER_CONFIG_NAMES.TECHNICIAN
					),
					filtersStateSlice
				);
				return val || undefined;
			},
			handleChange: (selectOption) => (val) =>
				updateFilters(
					filterOptionsBuilder(
						selectOption || FILTER_OPTIONS.IS,
						FILTER_CONFIG_NAMES.TECHNICIAN,
						val ? [val] : []
					)
				),
		}),
		[FILTER_CONFIG_NAMES.RESOLUTION_TYPE]: OWAsyncMultiSelectFilter({
			label: 'Resolution Type',
			fieldName: FILTER_CONFIG_NAMES.RESOLUTION_TYPE,
			placeholderText: 'Search for a resolution type',
			stateSlice: resolutionTypes,
			fetch: fetchResolutionTypes,
			fetchMultiple: fetchMultipleResolutionTypes,
			renderRecord: (rt) => (
				<div className="flex flex-row items-center">
					{rt.resolutionCode ? `${rt.resolutionCode} - ` : null}
					{rt.resolutionType}
				</div>
			),
			labelAccessor: (rt) => (
				<div className="flex flex-row items-center">
					{rt.resolutionCode ? `${rt.resolutionCode} - ` : null}
					{rt.resolutionType}
				</div>
			),
			component: OWAsyncTreeSelect,
		}),
		[FILTER_CONFIG_NAMES.IS_CAPEX]: OWRadioGroupFilter({
			fieldName: FILTER_CONFIG_NAMES.IS_CAPEX,
			items: [
				{ label: 'All', value: undefined },
				{ label: 'R&M', value: 'false' },
				{ label: 'CapEx', value: 'true' },
			],
			info: [
				{
					title: 'Repair and Maintenance (R&M)',
					description: 'Costs associated with repair and maintenance of existing assets',
				},
				{
					title: 'Capital Expenditures (CapEx)',
					description: 'Costs associated with buying new assets or upgrading existing assets',
				},
			],
		}),
		[FILTER_CONFIG_NAMES.CREATED_AT]: DateRangeSelectFilter({
			label: 'Created Between',
			labelPrefix: 'Created',
			fieldName: FILTER_CONFIG_NAMES.CREATED_AT,
		}),
		[FILTER_CONFIG_NAMES.COMPLETED_AT]: DateRangeSelectFilter({
			label: 'Completed Between',
			labelPrefix: 'Completed',
			fieldName: FILTER_CONFIG_NAMES.COMPLETED_AT,
		}),
		[FILTER_CONFIG_NAMES.LAST_SERVICE_CALL_SCHEDULED_AT]: DateRangeSelectFilter({
			label: 'Scheduled Between',
			labelPrefix: 'Scheduled',
			fieldName: FILTER_CONFIG_NAMES.LAST_SERVICE_CALL_SCHEDULED_AT,
		}),
		//[FILTER_CONFIG_NAMES.REGION]:,
		//[FILTER_CONFIG_NAMES.LOCAATION_TYPE]:,
		//[FILTER_CONFIG_NAMES.ASSET_VENDOR]:,
		[FILTER_CONFIG_NAMES.IS_STALE]: OWRadioGroupFilter({
			fieldName: FILTER_CONFIG_NAMES.IS_STALE,
			items: [
				{ label: 'All', value: undefined },
				{ label: 'Fresh', value: 'false' },
				{ label: 'Stale', value: 'true' },
			],
			info: [
				{ title: 'Fresh', description: `Work orders less than ${staleDays} days old.` },
				{ title: 'Stale', description: `Work orders older than ${staleDays} days.` },
			],
		}),
		[FILTER_CONFIG_NAMES.IS_PLANNED_MAINTENANCE]: OWRadioGroupFilter({
			fieldName: FILTER_CONFIG_NAMES.IS_PLANNED_MAINTENANCE,
			items: [
				{ label: 'All', value: undefined },
				{ label: 'Planned', value: 'true' },
				{ label: 'Reactive', value: 'false' },
			],
			info: [
				{
					title: 'Planned',
					description: `Scheduled maintenance work generated based on planned maintenance system.`,
				},
				{
					title: 'Reactive',
					description: `On demand maintenance work requested by your team or suggested by a service provider.`,
				},
			],
		}),
		//[FILTER_CONFIG_NAMES.INSTALL_AT]:,
		//[FILTER_CONFIG_NAMES.UPLOADED_AT]:,
		//[FILTER_CONFIG_NAMES.RECEIVED_AT]:,
		//[FILTER_CONFIG_NAMES.DUE_AT]:,
		//[FILTER_CONFIG_NAMES.PUBLISHED_AT]:,
		[FILTER_CONFIG_NAMES.APPROVED_AT]: DateRangeSelectFilter({
			label: 'Approved Between',
			labelPrefix: 'Approved',
			fieldName: FILTER_CONFIG_NAMES.APPROVED_AT,
		}),
		//[FILTER_CONFIG_NAMES.MARKED_PAID_AT]:,
		[FILTER_CONFIG_NAMES.IS_WARRANTY]: OWRadioGroupFilter({
			fieldName: FILTER_CONFIG_NAMES.IS_WARRANTY,
			items: [
				{ label: 'All', value: undefined },
				{ label: 'Warranty', value: 'true' },
				{ label: 'No Warranty', value: 'false' },
			],
			filtersValueAccessor: (filtersStateSlice) => {
				const val = filtersStateSlice[FILTER_CONFIG_NAMES.IS_WARRANTY];
				return typeof val !== 'undefined' && val !== null ? `${val}` : 'undefined';
			},
			handleChange: (val) => {
				const boolVal = val === 'true' ? true : val === 'false' ? false : undefined;
				updateFilters({ [FILTER_CONFIG_NAMES.IS_WARRANTY]: boolVal });
			},
			info: [
				{
					title: 'Warranty',
					description: 'Work orders where the associated asset is under warranty.',
				},
				{
					title: 'No Warranty',
					description: 'Work orders where the associated asset is not under warranty.',
				},
			],
		}),
		[FILTER_CONFIG_NAMES.IS_ASSET_DOWN]: OWRadioGroupFilter({
			fieldName: FILTER_CONFIG_NAMES.IS_ASSET_DOWN,
			items: [
				{ label: 'All', value: undefined },
				{ label: 'Asset Reported Offline', value: 'true' },
				{ label: 'Asset Online', value: 'false' },
			],
			info: [
				{
					title: 'Asset Reported Offline',
					description: 'Asset was reported offline during work order creation.',
				},
				{
					title: 'Asset Online',
					description: 'Asset was not reported offline during work order creation.',
				},
			],
		}),
		//[FILTER_CONFIG_NAMES.LAST_WALKTHROUGH_COMPLETED_AT]:,
		//[FILTER_CONFIG_NAMES.WALKTHROUGH_DUE_AT]:,
		//[FILTER_CONFIG_NAMES.WALKTHROUGH_COMPLETED_AT]:,
		//[FILTER_CONFIG_NAMES.UPDATED_AT]:,
		//[FILTER_CONFIG_NAMES.SHIPPED_AT]:,
		//[FILTER_CONFIG_NAMES.DISBURSED_AT]:,
		[FILTER_CONFIG_NAMES.ADDITIONAL_FIELD]: {
			label: 'Additional Fields',
			fieldName: FILTER_CONFIG_NAMES.ADDITIONAL_FIELD,
			type: FILTER_FIELD_TYPE.TUPLE,
			stateSlice: assetTypesAdditionalFields,
			filtersValueAccessor: (filtersStateSlice) => {
				const name = filtersStateSlice[getTupleNameFieldName(FILTER_CONFIG_NAMES.ADDITIONAL_FIELD)];
				const value =
					filtersStateSlice[getTupleValueFieldName(FILTER_CONFIG_NAMES.ADDITIONAL_FIELD)];
				return [name, value];
			},
			handleChange: (values) => {
				updateFilters({
					[getTupleNameFieldName(FILTER_CONFIG_NAMES.ADDITIONAL_FIELD)]: nullSafeGetOrElse(
						'0',
						values,
						undefined
					),
					[getTupleValueFieldName(FILTER_CONFIG_NAMES.ADDITIONAL_FIELD)]: nullSafeGetOrElse(
						'1',
						values,
						undefined
					),
				});
			},
			targetCollectionName: filterTargetCollectionName,
			fetch: fetchAssetTypesAdditionalFields,
			fetchMultiple: fetchMultipleAssetTypesAdditionalFields,
			valueAccessor: (item) => item.name,
			component: OWAsyncTupleSelect,
		},
		[FILTER_CONFIG_NAMES.IS_PAST_DUE]: OWRadioGroupFilter({
			fieldName: FILTER_CONFIG_NAMES.IS_PAST_DUE,
			items: [
				{ label: 'All', value: undefined },
				{ label: 'Past Due', value: 'true' },
				{ label: 'Not Past Due', value: 'false' },
			],
			info: [
				{ title: 'Past Due', description: 'Work orders that are past their due date.' },
				{
					title: 'Not Past Due',
					description: 'Work orders that have not yet passed their due date.',
				},
			],
		}),
		[FILTER_CONFIG_NAMES.IS_PAST_SCHEDULE]: OWRadioGroupFilter({
			fieldName: FILTER_CONFIG_NAMES.IS_PAST_SCHEDULE,
			items: [
				{ label: 'All', value: undefined },
				{ label: 'Past Schedule', value: 'true' },
				{ label: 'Not Past Schedule', value: 'false' },
			],
			info: [
				{ title: 'Past Schedule', description: 'Work orders that are past their scheduled date.' },
				{
					title: 'Not Past Schedule',
					description: 'Work orders that have not yet passed their scheduled date.',
				},
			],
		}),
		[FILTER_CONFIG_NAMES.IS_PAST_ESTIMATED_COMPLETION]: OWRadioGroupFilter({
			fieldName: FILTER_CONFIG_NAMES.IS_PAST_ESTIMATED_COMPLETION,
			items: [
				{ label: 'All', value: undefined },
				{ label: 'Past Est. Completion', value: 'true' },
				{ label: 'Not Past Est. Completion', value: 'false' },
			],
			info: [
				{
					title: 'Past Estimated Completion',
					description: 'Work orders that are past their estimated completion date.',
				},
				{
					title: 'Not Past Estimated Completion',
					description: 'Work orders that have not yet passed their estimated completion date.',
				},
			],
		}),
		[FILTER_CONFIG_NAMES.PM_SCHEDULE]: {
			label: 'PM Schedule',
			fieldName: FILTER_CONFIG_NAMES.PM_SCHEDULE,
			stateSlice: planned_maintenance_schedules,
			filtersValueAccessor: (filtersStateSlice) => {
				const id = nullSafeGet(FILTER_CONFIG_NAMES.PM_SCHEDULE, filtersStateSlice);
				return id;
			},
			hideFilter: true,
			keyAccessor: (item) => item.id,
			valueAccessor: (item) => `${item.id}`,
			handleChange: (id) => {
				updateFilters({
					[FILTER_CONFIG_NAMES.PM_SCHEDULE]: id || undefined,
				});
			},
			targetCollectionName: filterTargetCollectionName,
			fetch: fetchPMSchedule,
			fetchMultiple: fetchMultiplePMSchedule,
			renderItem: (item) => item.title,
		},
		[FILTER_CONFIG_NAMES.SITE_SURVEY_TASK]: {
			label: 'Site Survey Task',
			type: FILTER_FIELD_TYPE.STATIC,
			fieldName: FILTER_CONFIG_NAMES.SITE_SURVEY_TASK,
			stateSlice: planned_maintenance_schedules,
			filtersValueAccessor: (filtersStateSlice) => {
				const id = nullSafeGet(FILTER_CONFIG_NAMES.SITE_SURVEY_TASK, filtersStateSlice);
				return id;
			},
			hideFilter: true,
			keyAccessor: (item) => item.id,
			valueAccessor: (item) => `${item.id}`,
			handleChange: (id) => {
				updateFilters({
					[FILTER_CONFIG_NAMES.SITE_SURVEY_TASK]: id || undefined,
				});
			},
			values: ['Site Survey Task'],
			value: 'Site Survey Task',
			targetCollectionName: filterTargetCollectionName,
			fetch: () => {},
			fetchMultiple: () => {},
			renderItem: () => 'Selected',
		},
		//[FILTER_CONFIG_NAMES.AREA_ID_OR_NULL]:,
		[FILTER_CONFIG_NAMES.PUBLISHED_AT]: DateRangeSelectFilter({
			label: 'Published Between',
			labelPrefix: 'Published',
			fieldName: FILTER_CONFIG_NAMES.PUBLISHED_AT,
		}),
		[FILTER_CONFIG_NAMES.MARKED_PAID_AT]: DateRangeSelectFilter({
			label: 'Marked Paid Between',
			labelPrefix: 'Marked paid',
			fieldName: FILTER_CONFIG_NAMES.MARKED_PAID_AT,
		}),
		[FILTER_CONFIG_NAMES.HAS_GL_CODE]: OWRadioGroupFilter({
			fieldName: FILTER_CONFIG_NAMES.HAS_GL_CODE,
			items: [
				{ label: 'All', value: 'undefined' },
				{ label: 'Has GL Code', value: 'true' },
				{ label: 'No GL Code', value: 'false' },
			],
			filtersValueAccessor: (filtersStateSlice) => {
				const val = filtersStateSlice[FILTER_CONFIG_NAMES.HAS_GL_CODE];
				return typeof val === 'undefined' ? 'undefined' : `${val}`;
			},
			handleChange: (value) => {
				const val = value && value !== 'undefined' ? value === 'true' : undefined;
				updateFilters({ [FILTER_CONFIG_NAMES.HAS_GL_CODE]: val });
			},
			info: [
				{ title: 'Has GL Code', description: 'Invoices that have a GL code assigned.' },
				{ title: 'No GL Code', description: 'Invoices that do not have a GL code assigned.' },
			],
		}),
		[FILTER_CONFIG_NAMES.HAS_PO_NUMBER]: OWRadioGroupFilter({
			fieldName: FILTER_CONFIG_NAMES.HAS_PO_NUMBER,
			items: [
				{ label: 'All', value: 'undefined' },
				{ label: 'Has PO Number', value: 'true' },
				{ label: 'No PO Number', value: 'false' },
			],
			filtersValueAccessor: (filtersStateSlice) => {
				const val = filtersStateSlice[FILTER_CONFIG_NAMES.HAS_PO_NUMBER];
				return typeof val === 'undefined' ? 'undefined' : `${val}`;
			},
			handleChange: (value) => {
				const val = value && value !== 'undefined' ? value === 'true' : undefined;
				updateFilters({ [FILTER_CONFIG_NAMES.HAS_PO_NUMBER]: val });
			},
			info: [
				{ title: 'Has PO Number', description: 'Invoices that have a PO number assigned.' },
				{ title: 'No PO Number', description: 'Invoices that do not have a PO number assigned.' },
			],
		}),
		//[FILTER_CONFIG_NAMES.INTEGRATOR_NAME]:,
		//[FILTER_CONFIG_NAMES.INTEGRATOR_STATUS]:,
		[FILTER_CONFIG_NAMES.INSTALL_AT]: DateRangeSelectFilter({
			label: 'Installed Between',
			labelPrefix: 'Installed',
			fieldName: FILTER_CONFIG_NAMES.INSTALL_AT,
		}),
		[FILTER_CONFIG_NAMES.AREA_ID_OR_NULL]: OWAsyncMultiSelectFilter({
			label: 'Area',
			fieldName: FILTER_CONFIG_NAMES.AREA_ID_OR_NULL,
			placeholderText: 'Search for areas',
			stateSlice: areas,
			fetch: fetchAreas,
			fetchMultiple: fetchMultipleAreas,
			keyAccessor: (area) => area.id,
			valueAccessor: (area) => area.id,
			renderItem: (area) => area.name,
			additionalFiltersAccessor: areasLocationScopeAccessor,
			noteAccessor: areasLocationScopeNoteAccessor,
			filterOptions: [
				{ value: FILTER_OPTIONS.IS, label: 'is' },
				{ value: FILTER_OPTIONS.IS_NOT, label: 'is not' },
			],
		}),
		[FILTER_CONFIG_NAMES.SITE_SURVEY]: OWAsyncMultiSelectFilter({
			label: 'Site Survey',
			// Filters the walkthroughs list by title (matched against "<title> Walkthrough" on the
			// backend), not by site survey id — so options come from the grouped-by-title endpoint
			// and the stored value is the chosen title itself.
			fieldName: FILTER_CONFIG_NAMES.SITE_SURVEY_TITLE,
			placeholderText: 'Search for a site survey',
			stateSlice: siteSurveysGrouped,
			// Single-select: a walkthrough has exactly one title, and site survey titles are free
			// text (may contain commas), so a scalar value avoids the comma-join encoding the
			// multi-select filters rely on.
			mode: 'single',
			valueType: FILTER_VALUE_TYPE.STRING,
			// Only "is": the backend has no negated site-survey-title filter, so offering "is not"
			// would silently no-op.
			filterOptions: [{ value: FILTER_OPTIONS.IS, label: 'is' }],
			// The grouped endpoint groups by (title, templateId) and returns no id, so key/value the
			// options by title (also what we store and match on). Dedupe titles that repeat across
			// templates and inject `id = title` for the id-based tree-select machinery.
			fetch: (
				{ name },
				targetCollectionName,
				ignoredPagination,
				sorting,
				appliedFilters,
				addToTargetCollection
			) =>
				fetchSiteSurveysGrouped(
					{ search: name || undefined },
					targetCollectionName,
					{ current: 1, pageSize: 20 },
					sorting,
					appliedFilters,
					addToTargetCollection
				).then((rows) => {
					const seenTitles = new Set();
					return (rows || [])
						.filter((row) => {
							const title = nullSafeGet('title', row);
							if (!title || seenTitles.has(title)) return false;
							seenTitles.add(title);
							return true;
						})
						.map((row) => ({ ...row, id: row.title }));
				}),
			// The stored value is the human-readable title, so no id-hydration round trip is needed.
			fetchMultiple: () => Promise.resolve(),
			keyAccessor: (siteSurvey) => siteSurvey.title,
			valueAccessor: (siteSurvey) => siteSurvey.title,
			renderItem: (siteSurvey) => siteSurvey.title,
			selectedLabelAccessor: (siteSurvey) => siteSurvey.title,
			// Read/write the scalar title directly (no comma split/join) so titles containing commas
			// round-trip intact.
			filtersValueAccessor: (filtersStateSlice) => {
				const raw = nullSafeGet(FILTER_CONFIG_NAMES.SITE_SURVEY_TITLE, filtersStateSlice);
				return raw || undefined;
			},
			handleChange:
				(selectOption = FILTER_OPTIONS.IS) =>
				(value) => {
					const title = Array.isArray(value) ? value[0] : value;
					updateFilters({ [FILTER_CONFIG_NAMES.SITE_SURVEY_TITLE]: title || undefined });
				},
			component: OWAsyncTreeSelect,
		}),
		[FILTER_CONFIG_NAMES.PM_TEMPLATE]: OWAsyncMultiSelectFilter({
			label: 'PM Template',
			fieldName: FILTER_CONFIG_NAMES.PM_TEMPLATE,
			placeholderText: 'Search for a PM template',
			stateSlice: plannedMaintenanceTemplates,
			fetch: fetchWithDifferntSearchName(fetchPlannedMaintenanceTemplates, 'title'),
			fetchMultiple: fetchMultiplePlannedMaintenanceTemplates,
			renderItem: (template) => template.title,
			labelAccessor: (template) => template.title,
			component: OWAsyncTreeSelect,
		}),
		[FILTER_CONFIG_NAMES.DEPRECIATION_CLASS]: OWAsyncMultiSelectFilter({
			label: 'Depreciation Class',
			fieldName: FILTER_CONFIG_NAMES.DEPRECIATION_CLASS,
			placeholderText: 'Search for a depreciation class',
			stateSlice: depreciationClasses,
			fetch: fetchDepreciationClasses,
			fetchMultiple: fetchMultipleDepreciationClasses,
			component: OWAsyncTreeSelect,
		}),
		[FILTER_CONFIG_NAMES.REGION]: OWAsyncMultiSelectFilter({
			label: 'Region',
			fieldName: FILTER_CONFIG_NAMES.REGION,
			placeholderText: 'Search for a region',
			stateSlice: regions,
			fetch: fetchRegionsWithChildren,
			fetchMultiple: fetchMultipleRegions,
			component: OWAsyncTreeSelect,
		}),
		[FILTER_CONFIG_NAMES.LOCAATION_TYPE]: OWAsyncMultiSelectFilter({
			label: 'Location Type',
			fieldName: FILTER_CONFIG_NAMES.LOCAATION_TYPE,
			placeholderText: 'Search for a location type',
			stateSlice: locationTypes,
			fetch: fetchLocationTypes,
			fetchMultiple: fetchMultipleLocationTypes,
			component: OWAsyncTreeSelect,
		}),
		[FILTER_CONFIG_NAMES.WALKTHROUGH_COMPLETED_AT_KEY]: DateRangeSelectFilter({
			label: 'Walkthrough Completed Between',
			labelPrefix: 'Walkthrough Completed',
			fieldName: FILTER_CONFIG_NAMES.WALKTHROUGH_COMPLETED_AT,
		}),
		[FILTER_CONFIG_NAMES.DOWNTIME_STATUS]: OWRadioGroupFilter({
			fieldName: FILTER_CONFIG_NAMES.DOWNTIME_STATUS,
			items: [
				{ label: 'All', value: undefined },
				{ label: 'Online', value: 'online' },
				{ label: 'Offline', value: 'offline' },
			],
			filtersValueAccessor: (filtersStateSlice) => {
				const val = filtersStateSlice[FILTER_CONFIG_NAMES.DOWNTIME_STATUS];
				return typeof val === 'undefined' ? undefined : `${val}`;
			},
			handleChange: (val) => updateFilters({ [FILTER_CONFIG_NAMES.DOWNTIME_STATUS]: val }),
			info: [
				{ title: 'Online', description: 'Asset is working and in good condition.' },
				{ title: 'Offline', description: 'Asset is not working at this moment.' },
			],
		}),
		[FILTER_CONFIG_NAMES.ASSET_HAS_WARRANTY]: OWRadioGroupFilter({
			fieldName: FILTER_CONFIG_NAMES.ASSET_HAS_WARRANTY,
			items: [
				{ label: 'All', value: 'undefined' },
				{ label: 'Warranty', value: 'true' },
				{ label: 'No Warranty', value: 'false' },
			],
			filtersValueAccessor: (filtersStateSlice) => {
				const val = filtersStateSlice[FILTER_CONFIG_NAMES.ASSET_HAS_WARRANTY];
				return typeof val === 'undefined' ? 'undefined' : `${val}`;
			},
			handleChange: (hasWarranty) => {
				const val = hasWarranty === 'undefined' ? undefined : hasWarranty === 'true' ? true : false;
				updateFilters({ [FILTER_CONFIG_NAMES.ASSET_HAS_WARRANTY]: val });
			},
			info: [
				{ title: 'Warranty', description: 'Assets that are under warranty.' },
				{ title: 'No Warranty', description: 'Assets that are not under warranty.' },
			],
		}),
		[FILTER_CONFIG_NAMES.IS_ACTIVE]: OWRadioGroupFilter({
			fieldName: FILTER_CONFIG_NAMES.IS_ACTIVE,
			items: IS_ACTIVE_FILTER_ITEMS(false),
			filtersValueAccessor: (filtersStateSlice) => {
				const val = filtersStateSlice[FILTER_CONFIG_NAMES.IS_ACTIVE];
				return typeof val === 'undefined' ? undefined : `${val}`;
			},
			handleChange: (isActive) => {
				const boolVal = isActive === 'true' ? true : isActive === 'false' ? false : undefined;
				updateFilters({ [FILTER_CONFIG_NAMES.IS_ACTIVE]: boolVal });
			},
		}),
		[FILTER_CONFIG_NAMES.TOP_LEVEL_ONLY]: OWRadioGroupFilter({
			fieldName: FILTER_CONFIG_NAMES.TOP_LEVEL_ONLY,
			items: [
				{ label: 'Show Sub Assets', value: undefined },
				{ label: 'Hide Sub Assets', value: 'true' },
			],
			filtersValueAccessor: (filtersStateSlice) => {
				const val = filtersStateSlice[FILTER_CONFIG_NAMES.TOP_LEVEL_ONLY];
				return typeof val === 'undefined' ? undefined : `${val}`;
			},
			handleChange: (topLevelOnly) => {
				const boolVal = topLevelOnly === 'true' ? true : undefined;
				updateFilters({ [FILTER_CONFIG_NAMES.TOP_LEVEL_ONLY]: boolVal });
			},
			info: [
				{ title: 'Show Sub Assets', description: 'Include sub assets in the list.' },
				{ title: 'Hide Sub Assets', description: 'Show only top-level assets.' },
			],
		}),
		[FILTER_CONFIG_NAMES.ASSET_TAGGED]: OWRadioGroupFilter({
			fieldName: FILTER_CONFIG_NAMES.ASSET_TAGGED,
			items: [
				{ label: 'All', value: undefined },
				{ label: 'QR Tagged', value: 'true' },
				{ label: 'Not QR Tagged', value: 'false' },
			],
			filtersValueAccessor: (filtersStateSlice) => {
				const val = filtersStateSlice[FILTER_CONFIG_NAMES.ASSET_TAGGED];
				return typeof val === 'undefined' ? undefined : `${val}`;
			},
			handleChange: (val) => {
				const boolVal = val === 'true' ? true : val === 'false' ? false : undefined;
				updateFilters({ [FILTER_CONFIG_NAMES.ASSET_TAGGED]: boolVal });
			},
			info: [
				{ title: 'QR Tagged', description: 'Assets that have been QR tagged.' },
				{ title: 'Not QR Tagged', description: 'Assets that have not been QR tagged.' },
			],
		}),
		[FILTER_CONFIG_NAMES.REPLACEMENT_INDEX]: OWGenericMultiSelectFilter({
			label: 'Replacement Index',
			fieldName: FILTER_CONFIG_NAMES.REPLACEMENT_INDEX,
			options: REPLACEMENT_INDEX_STATIC_OPTIONS,
			handleChange: (ids) => {
				updateFilters({
					[FILTER_CONFIG_NAMES.REPLACEMENT_INDEX]: ids && ids.length ? ids.join(',') : undefined,
				});
			},
			showOutsideDropdown: false,
			placeholderText: 'Select replacement index',
		}),
		[FILTER_CONFIG_NAMES.METER_TYPE]: OWAsyncMultiSelectFilter({
			label: 'Meter Type',
			fieldName: FILTER_CONFIG_NAMES.METER_TYPE,
			placeholderText: 'Search for a meter type',
			stateSlice: meterTypes,
			fetch: fetchMeterTypes,
			fetchMultiple: fetchMultipleMeterTypes,
			component: OWAsyncTreeSelect,
		}),
		[FILTER_CONFIG_NAMES.DEFAULT_VENDORS]: OWAsyncMultiSelectFilter({
			label: 'Default Vendor',
			fieldName: FILTER_CONFIG_NAMES.DEFAULT_VENDORS,
			placeholderText: 'Search for a vendor',
			stateSlice: vendors,
			fetch: fetchVendors,
			fetchMultiple: fetchMultipleVendors,
		}),
		[FILTER_CONFIG_NAMES.PART_LABELS]: OWAsyncMultiSelectFilter({
			label: 'Labels',
			fieldName: FILTER_CONFIG_NAMES.PART_LABELS,
			placeholderText: 'Search for a label',
			stateSlice: labels,
			fetch: fetchLabels,
			fetchMultiple: fetchMultipleLabels,
			// Read options reactively from the SAME collection parts label creation
			// writes to (PartsLabelsManagementPage / PartsAddLabelButton -> create into
			// LabelEntityType.PART), so a newly created label appears here instantly.
			targetCollectionName: LabelEntityType.PART,
			renderItem: (tag) => tag.label || tag.name,
			component: LabelsDropdown,
		}),
		[FILTER_CONFIG_NAMES.SERIAL_NUMBER]: {
			label: 'Serial Number',
			fieldName: FILTER_CONFIG_NAMES.SERIAL_NUMBER,
			type: FILTER_FIELD_TYPE.STRING,
			valueType: FILTER_VALUE_TYPE.STRING,
			component: DebouncedFilterInput,
			placeholder: '',
			filterOptions: [
				{ value: FILTER_OPTIONS.IS, label: 'is' },
				{ value: FILTER_OPTIONS.CONTAINS, label: 'contains' },
			],
			targetCollectionName: filterTargetCollectionName,
			keyAccessor: (item) => item,
			valueAccessor: (item) => item,
			renderItem: (item) => item,
			renderRecord: (item) => item,
		},
		[FILTER_CONFIG_NAMES.MANUFACTURER]: {
			label: 'Manufacturer',
			fieldName: FILTER_CONFIG_NAMES.MANUFACTURER,
			type: FILTER_FIELD_TYPE.STRING,
			valueType: FILTER_VALUE_TYPE.STRING,
			component: DebouncedFilterInput,
			placeholder: 'Search for a manufacturer',
			filterOptions: [{ value: FILTER_OPTIONS.CONTAINS, label: 'contains' }],
			targetCollectionName: filterTargetCollectionName,
			keyAccessor: (item) => item,
			valueAccessor: (item) => item,
			renderItem: (item) => item,
			renderRecord: (item) => item,
		},
		[FILTER_CONFIG_NAMES.PART_MANUFACTURER_NAME]: getPartManufacturerNameFilter(
			filterTargetCollectionName
		),
	};
};

export const getPartManufacturerNameFilter = (targetCollectionName) => ({
	label: 'Manufacturer',
	fieldName: FILTER_CONFIG_NAMES.PART_MANUFACTURER_NAME,
	type: FILTER_FIELD_TYPE.STRING,
	valueType: FILTER_VALUE_TYPE.STRING,
	component: DebouncedFilterInput,
	placeholder: 'Search for a manufacturer',
	filterOptions: [
		{ value: FILTER_OPTIONS.IS, label: 'is' },
		{ value: FILTER_OPTIONS.CONTAINS, label: 'contains' },
	],
	targetCollectionName,
	keyAccessor: (item) => item,
	valueAccessor: (item) => item,
	renderItem: (item) => item,
	renderRecord: (item) => item,
});
